import { Badge } from '../../components/Badge';
import { cn } from '../../utils/cn';
import {
  leaderboardRankPodiumColour,
  leaderboardRankTextClass,
} from '../../utils/leaderboardRank';
import type { LeaderboardRankChipProps } from './LeaderboardRankChip.types';

/**
 * Rank slot at the start of every leaderboard row, on web and mobile.
 *
 * Podium chips for #1 (gold), #2 (silver), #3 (bronze); past that a plain
 * centred number on the neutral chip. The empty-state widget passes
 * `rank='-'` for the placeholder shown when a group has fewer than 2 members.
 *
 * It is the DS `Badge` in counter mode, which is what gives it the 24pt box,
 * the `min-w` that lets a deep rank widen the chip instead of clipping it, and
 * the podium fills (the `gold` / `silver` / `bronze` colours exist for exactly
 * this chip). Two deltas from a stock counter are deliberate:
 *
 *   - `rounded-lg`, not the counter's pill radius — the podium is a rounded
 *     SQUARE in Figma, and at one digit a pill would read as a dot.
 *   - the numeral rides the leaderboard row's own type ramp (16px, stepping to
 *     12px past 3 digits) rather than the badge's 10px XS label, so the rank
 *     carries the same weight as the MP value across the row.
 *
 * ONE component for both platforms on purpose: web and mobile each had their
 * own copy of this chip, and the copies drifted (a DS sweep put mobile on a
 * stock 32pt counter with no silver or bronze while web kept all three).
 */
export const LeaderboardRankChip = ({
  rank,
  className,
  testID,
}: LeaderboardRankChipProps) => {
  const colour = leaderboardRankPodiumColour(rank);
  return (
    <Badge
      type="count"
      size="xs"
      colour={colour}
      testID={testID}
      className={cn('rounded-lg', className)}
      textClassName={cn('tabular-nums', leaderboardRankTextClass(rank))}>
      {rank}
    </Badge>
  );
};
